import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CircleCheck, OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useLanguageSync } from '@/common/hooks/useLanguageSync'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

type EditUserPreferencesParams = {
  userId: string
  displayName?: string
  locale?: string
  allowedPosts?: boolean
  allowedApplications?: string[]
  registeredApplications?: string[]
}

async function editUserPreferences(data: EditUserPreferencesParams) {
  return api.backend.post('/user-preference', data)
}

export function useEditUserPreferences() {
  const { t } = useTranslation('profile')
  const { syncLanguageToPreference } = useLanguageSync()
  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    EditUserPreferencesParams
  >({
    mutationKey: ['/user-preference'],
    mutationFn: editUserPreferences,
    onSuccess: (_, variables) => {
      if (variables.locale) {
        syncLanguageToPreference(variables.locale)
      }

      toast(Toast, {
        data: {
          type: 'success',
          icon: CircleCheck,
          title: t('preferences.toast.success.title'),
          description: t('preferences.toast.success.message'),
        },
      })
      queryClient.refetchQueries({ queryKey: ['/user/get-user-info'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('preferences.toast.error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('preferences.toast.error.message'),
        },
      })
    },
  })

  const handleEditUserPreferences = async (data: EditUserPreferencesParams) => {
    await mutateAsync(data)
  }

  return {
    handleEditUserPreferences,
    isPendingEditUserPreferences: isPending,
  }
}
